import React, {
  useState,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useId,
} from 'react'
import isFunction from 'lodash/isFunction'
import Dropdown from 'react-bootstrap/Dropdown'
import FormCheckInput from 'react-bootstrap/FormCheckInput'
import palette from 'src/designSystem/variables/palette'
import { useHasRequiredCapabilities } from 'src/app/AppAuth/AppAuth.hooks'
import { StyledDropdownToggle } from '../StandardDropdown/StandardDropdown.styles'
import { CountChip } from '../Chips/BasicChip'
import { DropdownLabel } from '../Dropdown/styles'
import { FlexAlign, FlexRow } from '../Flex'
import { DropdownSearchInput } from '../Inputs/DropdownSearchInput'
import { DropdownSelectedIcon } from '../Dropdown/styles'
import { OverflowDropdownToggle } from '../Dropdown/OverflowDropdownToggle'
import { StyledInputError } from '../Inputs/TextInput/styles'
import { Tooltip, TooltipPositions } from '../Tooltip/Tooltip'
import {
  DropdownContainer,
  ItemHint,
  RowLabel,
  SelectDropdownLabel,
  SelectDropDownSelected,
  SelectMenu,
} from './SelectDropdown.styles'
import {
  SelectItem,
  SelectDropdownProps,
  DropdownAutoClose,
  DROPDOWN_SEARCH_THRESHOLD,
} from './SelectDropdown.constants'

export const SelectDropdown = ({
  items,
  label,
  labelButton,
  defaultValues,
  placeholder,
  isMultiSelect,
  isRequired,
  errMsg,
  isWarning,
  onSelectCb,
  formatLabel,
  alwaysShowError,
  requiredCapabilities,
  resource,
  isDisabled,
  isBoldLabel,
  customOnToggle,
  hideError,
  prefix,
  isSearchable,
  categories,
  hideLabel,
  overflowToggle,
}: SelectDropdownProps): JSX.Element => {
  const hasCapabilities_ = useHasRequiredCapabilities(
    requiredCapabilities,
    resource,
  )
  const hasPermission = hasCapabilities_
  // We assume name is unique for our dropdown lists. Value breaks for the Provider use-case since
  // custom Providers all share the same "Other" enum value.

  const [searchQuery, setSearchQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [selected, setSelected] = useState<SelectItem[]>([])
  const [selectedCheckboxes, setSelectedCheckboxes] = useState<
    Set<string | number>
  >(new Set())
  const hasSelected = selected.length > 0
  const showChip = isMultiSelect && selected.length > 0
  const autoCloseSetting: DropdownAutoClose = isMultiSelect ? 'outside' : true
  const selectedValue = selected.length
    ? isMultiSelect
      ? selected
          .map((item: SelectItem) =>
            typeof item.name === 'string' ? item.name : String(item.value),
          )
          .join(', ')
      : selected[0].name
    : placeholder || 'Select an Item'
  const showError = alwaysShowError || (isRequired && !hasSelected)

  useEffect(() => {
    // If default values change, be sure to update the selected state
    const preSelected = defaultValues ? [...defaultValues] : []
    const preSelectedSet = new Set(
      preSelected.map((item: SelectItem) => item.value),
    )
    setSelected(preSelected)
    isMultiSelect && setSelectedCheckboxes(preSelectedSet)
  }, [defaultValues, isMultiSelect])

  const onSelectItem = useCallback(
    (item: SelectItem) => {
      if (isDisabled || !hasPermission || item.disabled) return
      if (!isMultiSelect) {
        setSelected([item])
        onSelectCb([item])
      } else {
        const isChecked = selectedCheckboxes.has(item.value)
        const selectedItems = isChecked
          ? selected.filter(
              (selectedItem: SelectItem) => item.value !== selectedItem.value,
            )
          : [...selected, item]
        setSelectedCheckboxes((newCheckboxes) => {
          const newSet = new Set(newCheckboxes)
          isChecked ? newSet.delete(item.value) : newSet.add(item.value)
          return newSet
        })
        setSelected(selectedItems)
        onSelectCb(selectedItems)
      }
    },
    [
      isMultiSelect,
      onSelectCb,
      selected,
      selectedCheckboxes,
      isDisabled,
      hasPermission,
    ],
  )

  // There is a weird bug where if you make toggles and hit the escape key, the component crashes
  const toggleOption = (item: SelectItem, e?: MouseEvent) => {
    e?.preventDefault()
    isMultiSelect && e?.stopPropagation() // Prevents dropdown from closing when clicking on checkbox
    onSelectItem(item)
  }

  const filteredItems = useMemo(() => {
    if (!isSearchable || !searchQuery) return items ?? []
    const q = searchQuery.toLowerCase()
    return (items ?? []).filter((item) =>
      typeof item.name === 'string'
        ? item.name.toLowerCase().includes(q)
        : String(item.value).toLowerCase().includes(q),
    )
  }, [isSearchable, items, searchQuery])

  const displayLabel = isFunction(formatLabel) ? formatLabel(label) : label
  const labelTextId = useId()

  const isDisabledOrNoPermission =
    isDisabled || (!isMultiSelect && !hasPermission)

  const renderItem = (item: SelectItem, indent = false) => {
    const isChecked = selectedCheckboxes.has(item.value)
    return (
      <Dropdown.Item
        key={`dropdown-item-${item.value}`}
        eventKey={`dropdown-item-${item.value}`}
        onClick={(e: MouseEvent) => toggleOption(item, e)}
        disabled={isDisabled || !hasPermission || item.disabled}
      >
        <FlexRow
          align={FlexAlign.CENTER}
          justify={FlexAlign.FLEX_START}
          isFullWidth
          style={indent ? { paddingLeft: '16px' } : undefined}
        >
          {isMultiSelect && (
            <FormCheckInput
              id={`check-input-dropdown-item-${labelTextId}-${item.value}`}
              onChange={() => toggleOption(item)}
              checked={isChecked}
              disabled={isDisabled || !hasPermission || item.disabled}
            />
          )}
          <Tooltip
            id={`dropdown-item-tooltip-${labelTextId}-${item.value}`}
            placement={TooltipPositions.top}
            tooltipBody={item.name}
          >
            <RowLabel $isMultiSelect={isMultiSelect}>{item.name}</RowLabel>
          </Tooltip>
          {item.hint && <ItemHint>{item.hint}</ItemHint>}
          {!isMultiSelect && selected.some((s) => s.value === item.value) && (
            <DropdownSelectedIcon />
          )}
        </FlexRow>
      </Dropdown.Item>
    )
  }

  const renderItems = () => {
    if (categories && categories.length > 0) {
      const grouped: Record<string, SelectItem[]> = {}
      const uncategorized: SelectItem[] = []
      categories.forEach((cat) => {
        grouped[cat.id] = []
      })
      filteredItems.forEach((item) => {
        if (item.categoryId && grouped[item.categoryId]) {
          grouped[item.categoryId].push(item)
        } else {
          uncategorized.push(item)
        }
      })
      return (
        <>
          {categories.map((cat) => {
            const catItems = grouped[cat.id] || []
            if (catItems.length === 0) return null
            return (
              <React.Fragment key={`category-group-${cat.id}`}>
                <div
                  style={{
                    padding: '6px 16px',
                    fontWeight: 'bold',
                    color: palette.neutral[700],
                    fontSize: '11px',
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                  }}
                >
                  {cat.name}
                </div>
                {catItems.map((item) => renderItem(item, true))}
              </React.Fragment>
            )
          })}
          {uncategorized.length > 0 && (
            <React.Fragment key="category-group-uncategorized">
              <div
                style={{
                  padding: '6px 16px',
                  fontWeight: 'bold',
                  color: palette.neutral[700],
                  fontSize: '11px',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                }}
              >
                Uncategorized
              </div>
              {uncategorized.map((item) => renderItem(item, true))}
            </React.Fragment>
          )}
        </>
      )
    }
    return filteredItems.map((item) => renderItem(item))
  }

  return (
    <DropdownContainer
      $hasSelected={
        hasSelected || (!!placeholder && typeof placeholder !== 'string')
      }
      $isDisabled={isDisabled}
    >
      <DropdownLabel>
        {!hideLabel &&
          (labelButton ? (
            <FlexRow align={FlexAlign.CENTER} justify={FlexAlign.SPACE_BETWEEN}>
              <span id={labelTextId}>{displayLabel}</span>
              {labelButton}
            </FlexRow>
          ) : (
            <SelectDropdownLabel id={labelTextId} $isbold={isBoldLabel}>
              {displayLabel}
            </SelectDropdownLabel>
          ))}
        <Dropdown
          id={`select-dropdown-${label}`}
          show={isOpen && !isDisabledOrNoPermission}
          autoClose={autoCloseSetting}
          onToggle={(open) => {
            setIsOpen(open)
            if (!open) setSearchQuery('')
            else customOnToggle?.()
          }}
        >
          <Dropdown.Toggle
            as={StyledDropdownToggle}
            aria-labelledby={hideLabel ? undefined : labelTextId}
            aria-label={hideLabel ? label : undefined}
            color={palette.neutral[400]}
            borderColor={palette.neutral[100]}
            width="100%"
            disabled={isDisabledOrNoPermission}
          >
            <FlexRow justify={FlexAlign.SPACE_BETWEEN} gap={8}>
              {prefix && (
                <FlexRow align={FlexAlign.CENTER} justify={FlexAlign.CENTER}>
                  {prefix}
                </FlexRow>
              )}
              {overflowToggle && isMultiSelect ? (
                // Inside SelectDropDownSelected for the shared text colours
                // and so it gets the full width the overflow maths measures.
                <SelectDropDownSelected className="selected-value">
                  <OverflowDropdownToggle
                    items={selected}
                    getDisplayName={(item) => String(item.name)}
                    getKey={(item) => String(item.value)}
                    placeholder={
                      typeof placeholder === 'string' ? placeholder : 'Select'
                    }
                    tooltipId={`select-dropdown-overflow-${label}`}
                  />
                </SelectDropDownSelected>
              ) : (
                <>
                  <SelectDropDownSelected className="selected-value">
                    {selectedValue}
                  </SelectDropDownSelected>
                  {showChip ? (
                    <CountChip ml="xxs">{selected.length}</CountChip>
                  ) : null}
                </>
              )}
            </FlexRow>
          </Dropdown.Toggle>
          <SelectMenu>
            {isSearchable &&
              ((items ?? []).length > DROPDOWN_SEARCH_THRESHOLD ||
                !!searchQuery) && (
                <DropdownSearchInput
                  value={searchQuery}
                  onChange={setSearchQuery}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus={isOpen}
                />
              )}
            {renderItems()}
          </SelectMenu>
        </Dropdown>
      </DropdownLabel>
      {!hideError && (
        <StyledInputError showError={!!showError} isWarning={isWarning}>
          {errMsg}
        </StyledInputError>
      )}
    </DropdownContainer>
  )
}
